import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  useTheme,
  useMediaQuery,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Description as DescriptionIcon,
  CameraAlt as CameraIcon,
  Palette as PaletteIcon,
  Calculate as CalculateIcon,
  Speed as SpeedIcon,
  AccountBalanceWallet as AccountIcon,
  CreditCard as PaymentIcon,
  CheckCircle as CheckIcon,
  Timer as TimerIcon,
  Security as SecurityIcon,
  CloudDone as CloudDoneIcon,
  Email as EmailIcon,
  AutoAwesome as AutoAwesomeIcon,
  TrendingUp as TrendingUpIcon,
  ReceiptLong as ReceiptIcon,
} from '@mui/icons-material';
import Logo from '../components/Logo';
import { useAuthStore } from '../stores/useAuthStore';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated } = useAuthStore();

  // Rediriger vers le dashboard si l'utilisateur est déjà connecté
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const keyBenefits = [
    {
      icon: <TimerIcon sx={{ fontSize: 50, color: 'secondary.main' }} />,
      title: 'Devis en 2 min',
      description: 'Créez des devis professionnels en quelques clics avec calcul automatique',
      highlight: 'Gain de temps',
    },
    {
      icon: <CameraIcon sx={{ fontSize: 50, color: 'secondary.main' }} />,
      title: 'Photos géolocalisées',
      description: 'Documentez vos chantiers avec photos avant/après et localisation GPS',
      highlight: 'Professionnel',
    },
    {
      icon: <ReceiptIcon sx={{ fontSize: 50, color: 'secondary.main' }} />,
      title: 'Dépenses en 1 photo',
      description: 'Prenez en photo vos tickets, reçus et factures. Saisie rapide sur le terrain !',
      highlight: 'Ultra pratique',
    },
    {
      icon: <PaletteIcon sx={{ fontSize: 50, color: 'secondary.main' }} />,
      title: 'Personnalisation totale',
      description: 'Logo, couleurs, polices... Vos documents reflètent votre image de marque',
      highlight: 'À votre image',
    },
    {
      icon: <AccountIcon sx={{ fontSize: 50, color: 'secondary.main' }} />,
      title: 'Compta simplifiée',
      description: 'Export comptable et envoi direct à votre expert-comptable en 1 clic',
      highlight: 'Export Excel/CSV',
    },
    {
      icon: <PaymentIcon sx={{ fontSize: 50, color: 'secondary.main' }} />,
      title: 'Paiement CB sécurisé',
      description: 'Vos clients paient en ligne par carte bancaire de manière sécurisée',
      highlight: 'Encaissement rapide',
    },
  ];

  const features = [
    {
      icon: <DescriptionIcon />,
      title: 'Devis & Factures',
      items: [
        'Création rapide en 2 minutes',
        'Calcul automatique des montants',
        'Modèles personnalisables',
        'PDF professionnel',
        'Envoi par email',
      ],
    },
    {
      icon: <CameraIcon />,
      title: 'Gestion Chantier',
      items: [
        'Photos avant/après',
        'Géolocalisation GPS',
        'Albums par projet',
        'Partage avec clients',
        'Stockage illimité',
      ],
    },
    {
      icon: <AccountIcon />,
      title: 'Comptabilité',
      items: [
        'Saisie dépenses avec photo',
        'Tickets, reçus, factures',
        'Tableau de bord complet',
        'Suivi CA et dépenses',
        'Export compta Excel/CSV',
        'Envoi direct comptable',
        'Alertes seuils TVA',
      ],
    },
    {
      icon: <PaymentIcon />,
      title: 'Paiements',
      items: [
        'Paiement CB en ligne',
        'Transaction sécurisée',
        'Suivi des encaissements',
        'Relances automatiques',
        'Multi-moyens de paiement',
      ],
    },
    {
      icon: <PaletteIcon />,
      title: 'Personnalisation',
      items: [
        'Upload votre logo',
        'Couleurs personnalisées',
        'Polices au choix',
        'Mentions légales',
        'Templates sur mesure',
      ],
    },
    {
      icon: <CalculateIcon />,
      title: 'Outils Pro',
      items: [
        'Calculateurs surfaces',
        'Estimation matériaux',
        'Calculateur fournitures',
        'Base de données prix',
        'Gestion stock',
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation Header */}
      <Box
        sx={{
          bgcolor: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Logo size={isMobile ? 50 : 60} />
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  letterSpacing: '-0.5px',
                }}
              >
                Sersal
              </Typography>
            </Box>
            <Stack direction="row" spacing={isMobile ? 1 : 2}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate('/login')}
                size={isMobile ? 'small' : 'medium'}
              >
                Connexion
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate('/register')}
                size={isMobile ? 'small' : 'medium'}
              >
                Inscription
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          color: 'white',
          py: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Décorations de fond */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 300,
            height: 300,
            borderRadius: '50%',
            bgcolor: 'secondary.main',
            opacity: 0.1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -100,
            left: -100,
            width: 400,
            height: 400,
            borderRadius: '50%',
            bgcolor: 'secondary.main',
            opacity: 0.08,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              {/* Badge "Offre spéciale" */}
              <Chip
                icon={<AutoAwesomeIcon />}
                label="1 MOIS GRATUIT - SANS CARTE BANCAIRE"
                sx={{
                  bgcolor: alpha(theme.palette.secondary.main, 0.2),
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  px: 1,
                  py: 0.5,
                  mb: 3,
                  border: `2px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
                }}
              />

              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                sx={{
                  fontWeight: 900,
                  mb: 2,
                  lineHeight: 1.1,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
                }}
              >
                La solution N°1 pour les artisans du bâtiment
              </Typography>
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                sx={{
                  mb: 4,
                  opacity: 0.95,
                  fontWeight: 400,
                  lineHeight: 1.4,
                }}
              >
                Devis en 2 min • Photos de chantier • Compta simplifiée • Paiement CB sécurisé
              </Typography>

              {/* Points clés */}
              <Stack spacing={1.5} sx={{ mb: 4 }}>
                {[
                  'Créez vos devis professionnels en 2 minutes',
                  'Gérez vos photos de chantier avec géolocalisation',
                  'Saisissez vos dépenses en 1 photo (tickets, reçus, factures)',
                  'Export comptable direct vers votre expert-comptable',
                  'Vos clients paient en ligne par carte bancaire',
                  'Personnalisez vos documents avec votre logo',
                ].map((text, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckIcon sx={{ color: 'secondary.main', fontSize: 24 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {text}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing={2}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  startIcon={<AutoAwesomeIcon />}
                  sx={{
                    bgcolor: 'secondary.main',
                    color: 'white',
                    px: 5,
                    py: 2,
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                    '&:hover': {
                      bgcolor: 'secondary.dark',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.4)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Essayer gratuitement
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.15)',
                      borderWidth: 2,
                    },
                  }}
                >
                  Se connecter
                </Button>
              </Stack>

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 2,
                  opacity: 0.8,
                  fontSize: '0.9rem',
                }}
              >
                Sans engagement • Sans carte bancaire • Annulation à tout moment
              </Typography>
            </Grid>

            {/* Illustration/Screenshot placeholder */}
            <Grid item xs={12} md={5}>
              <Paper
                elevation={10}
                sx={{
                  p: 2,
                  bgcolor: alpha('#fff', 0.95),
                  borderRadius: 3,
                  transform: isMobile ? 'none' : 'rotate(2deg)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: isMobile ? 'none' : 'rotate(0deg) scale(1.02)',
                  },
                }}
              >
                <Box
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    minHeight: 300,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <TrendingUpIcon sx={{ fontSize: 80, color: 'white', mb: 2 }} />
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                    +300%
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', opacity: 0.9 }}>
                    Productivité en moyenne
                  </Typography>
                  <Divider sx={{ my: 2, bgcolor: 'rgba(255,255,255,0.3)', width: '80%' }} />
                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
                    Rejoignez des centaines d'artisans satisfaits
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Key Benefits Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            label="LES AVANTAGES"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              fontWeight: 700,
              mb: 2,
            }}
          />
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            sx={{
              fontWeight: 800,
              color: 'primary.main',
              mb: 2,
            }}
          >
            Pourquoi choisir Sersal ?
          </Typography>
          <Typography
            variant={isMobile ? 'body1' : 'h6'}
            sx={{
              color: 'text.secondary',
              maxWidth: 700,
              mx: 'auto',
            }}
          >
            Une solution complète pour digitaliser votre entreprise et gagner du temps au quotidien
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {keyBenefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'visible',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {/* Badge highlight */}
                <Chip
                  label={benefit.highlight}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -12,
                    right: 16,
                    bgcolor: 'secondary.main',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                  }}
                />

                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      mb: 3,
                    }}
                  >
                    {benefit.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: 'primary.main',
                    }}
                  >
                    {benefit.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Detailed Features Section */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="FONCTIONNALITÉS"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                fontWeight: 700,
                mb: 2,
              }}
            />
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              sx={{
                fontWeight: 800,
                color: 'primary.main',
                mb: 2,
              }}
            >
              Tout ce dont vous avez besoin
            </Typography>
            <Typography
              variant={isMobile ? 'body1' : 'h6'}
              sx={{
                color: 'text.secondary',
                maxWidth: 700,
                mx: 'auto',
              }}
            >
              Une suite complète d'outils pensée pour les artisans du bâtiment
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  elevation={2}
                  sx={{
                    height: '100%',
                    p: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                        color: 'secondary.main',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                      }}
                    >
                      {feature.title}
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    {feature.items.map((item, idx) => (
                      <ListItem key={idx} disableGutters>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item}
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: 'text.secondary',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            label="TARIFS"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              fontWeight: 700,
              mb: 2,
            }}
          />
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            sx={{
              fontWeight: 800,
              color: 'primary.main',
              mb: 2,
            }}
          >
            Simple et transparent
          </Typography>
          <Typography
            variant={isMobile ? 'body1' : 'h6'}
            sx={{
              color: 'text.secondary',
              maxWidth: 700,
              mx: 'auto',
            }}
          >
            Commencez gratuitement, sans carte bancaire. Évoluez quand vous êtes prêt.
          </Typography>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: alpha(theme.palette.secondary.main, 0.1), borderRadius: 2, textAlign: 'center' }}>
          <AutoAwesomeIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'secondary.main', mb: 1 }}>
            1 mois d'essai Premium gratuit
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            Testez <strong>toutes les fonctionnalités Premium</strong> pendant 30 jours sans carte bancaire.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            À la fin de l'essai, <strong>choisissez l'offre qui vous convient</strong> : Essential ou Premium. Aucun engagement !
          </Typography>
        </Box>

        <Grid container spacing={4} justifyContent="center">
          {/* Plan Essential */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                Essential
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Pour les artisans
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.main' }}>
                    12,9€
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    /mois
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  ou 129€/an (HT)
                </Typography>
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                  Économisez 17% en annuel
                </Typography>
              </Box>
              <List dense sx={{ mb: 3, flex: 1 }}>
                {[
                  'Devis & Factures illimités',
                  'Gestion clients',
                  'Photos de chantier',
                  'Planning des chantiers',
                  'Calculateurs métiers',
                  'Support standard',
                ].map((item, idx) => (
                  <ListItem key={idx} disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon sx={{ color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => navigate('/register')}
                sx={{ py: 1.5 }}
              >
                Commencer
              </Button>
            </Paper>
          </Grid>

          {/* Plan Premium */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={8}
              sx={{
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                border: `3px solid ${theme.palette.secondary.main}`,
                transform: { xs: 'none', md: 'scale(1.05)' },
              }}
            >
              <Chip
                label="RECOMMANDÉ"
                size="small"
                sx={{
                  position: 'absolute',
                  top: -12,
                  right: 20,
                  bgcolor: 'secondary.main',
                  color: 'white',
                  fontWeight: 700,
                }}
              />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'secondary.main' }}>
                Premium
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Pour les professionnels
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: 'secondary.main' }}>
                    19,9€
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    /mois
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  ou 199€/an (HT)
                </Typography>
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                  Économisez 17% en annuel
                </Typography>
              </Box>
              <List dense sx={{ mb: 3, flex: 1 }}>
                {[
                  'Tout Essential +',
                  '✍️ Signature électronique',
                  '💳 Paiement CB intégré',
                  '📊 Comptabilité automatisée',
                  'Export comptable',
                  'Rapports avancés',
                  'Support prioritaire',
                ].map((item, idx) => (
                  <ListItem key={idx} disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon sx={{ color: 'secondary.main' }} />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => navigate('/register')}
                sx={{
                  py: 1.5,
                  bgcolor: 'secondary.main',
                  '&:hover': {
                    bgcolor: 'secondary.dark',
                  },
                }}
              >
                Essayer gratuitement
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Trust Section */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), py: { xs: 4, md: 5 } }}>
        <Container maxWidth="md">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={4} textAlign="center">
              <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 0.5 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                100%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sécurisé et conforme RGPD
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} textAlign="center">
              <CloudDoneIcon sx={{ fontSize: 48, color: 'primary.main', mb: 0.5 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                99,9%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Disponibilité garantie
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} textAlign="center">
              <EmailIcon sx={{ fontSize: 48, color: 'primary.main', mb: 0.5 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                24h
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Support réactif
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
          color: 'white',
          py: { xs: 6, md: 7 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Décorations */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            bgcolor: alpha('#fff', 0.08),
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <AutoAwesomeIcon sx={{ fontSize: 50, mb: 1.5 }} />
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              sx={{
                fontWeight: 800,
                mb: 1.5,
                lineHeight: 1.2,
              }}
            >
              Prêt à transformer votre activité ?
            </Typography>
            <Typography
              variant={isMobile ? 'body1' : 'h6'}
              sx={{
                mb: 3,
                opacity: 0.95,
                fontWeight: 400,
              }}
            >
              Rejoignez des centaines d'artisans qui ont choisi Sersal pour professionnaliser leur
              entreprise du bâtiment
            </Typography>

            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={2}
              sx={{ justifyContent: 'center', mb: 2 }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => navigate('/register')}
                sx={{
                  bgcolor: 'white',
                  color: 'secondary.main',
                  px: 5,
                  py: 1.8,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  '&:hover': {
                    bgcolor: 'grey.100',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Essayer gratuitement 1 mois
              </Button>
            </Stack>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', opacity: 0.9 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckIcon fontSize="small" />
                <Typography variant="body2">Sans carte bancaire</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckIcon fontSize="small" />
                <Typography variant="body2">Sans engagement</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckIcon fontSize="small" />
                <Typography variant="body2">Annulation en 1 clic</Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          bgcolor: 'primary.dark',
          color: 'white',
          py: 4,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Logo size={30} variant="white" />
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                © 2026 Sersal - Gestion pour artisans du bâtiment
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Made with ❤️ for construction professionals
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
